import React, { useState } from 'react';
import { Search, Users, Calendar, FileText, Edit, Eye, ChevronDown, ChevronLeft, ChevronRight, FolderOpen } from 'lucide-react';
import useGroupProjectManagementStore from '@/stores/TrainerStore/GroupProjectManagementStore';
import LoadingSpinner from '@/Components/common/LoadingSpinner';
import ErrorDisplay from '@/Components/common/ErrorDisplay';
import './ProjectList.css';

const ITEMS_PER_PAGE = 12;

interface ProjectListProps {
  onCreateClick?: () => void;
  onViewProject?: (project: any) => void;
  onEditProject?: (project: any) => void;
}

const ProjectList: React.FC<ProjectListProps> = ({ onCreateClick, onViewProject, onEditProject }) => {
  const { projects, loading, error, fetchProjects } = useGroupProjectManagementStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const filteredProjects = projects?.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || project.status === selectedStatus;
    return matchesSearch && matchesStatus;
  }) || [];

  // Pagination logic
  const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProjects = filteredProjects.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatus]);

  // Loading state
  if (loading && !projects) {
    return (
      <div className="trainer-group-project-list-container">
        <LoadingSpinner size="lg" message="Loading projects..." fullScreen={false} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="trainer-group-project-list-container">
        <ErrorDisplay
          title="Failed to Load Projects"
          message={error}
          showRetry={true}
          onRetry={fetchProjects}
          type="error"
        />
      </div>
    );
  }

  return (
    <div className="trainer-group-project-list-container">
      <div className="trainer-group-project-list-wrapper">
        {/* Search and Filter Section */}
        <div className="trainer-group-project-filters">
          <div className="trainer-group-search-box">
            <Search size={20} className="trainer-group-search-icon" />
            <input
              type="text"
              placeholder="Search Projects"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="trainer-group-search-input"
            />
          </div>

          <div className="trainer-group-filter-dropdown">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="trainer-group-status-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="completed">Completed</option>
            </select>
            <ChevronDown size={20} className="trainer-group-dropdown-icon" />
          </div>
        </div>

        {/* Empty State */}
        {filteredProjects.length === 0 && (
          <div className="trainer-group-empty-state">
            <div className="trainer-group-empty-state-content">
              <div className="trainer-group-empty-state-icon">
                <FolderOpen size={64} />
              </div>
              <h3 className="trainer-group-empty-state-title">
                {searchTerm || selectedStatus !== 'all'
                  ? 'No projects found'
                  : 'No projects yet'}
              </h3>
              <p className="trainer-group-empty-state-message">
                {searchTerm || selectedStatus !== 'all'
                  ? 'Try adjusting your search or filters to find what you\'re looking for.'
                  : 'Get started by creating your first group project.'}
              </p>
              {!searchTerm && selectedStatus === 'all' && onCreateClick && (
                <button
                  className="trainer-group-empty-state-button"
                  onClick={onCreateClick}
                >
                  + Create Your First Project
                </button>
              )}
            </div>
          </div>
        )}

        {/* Projects Grid */}
        {filteredProjects.length > 0 && (
          <>
            <div className="trainer-group-projects-grid">
              {paginatedProjects.map((project) => (
            <div key={project.id} className="trainer-group-project-card">
              {/* Header with Icon and Status */}
              <div className="trainer-group-project-card-header">
                <div className="trainer-group-project-icon">
                  <Users size={28} />
                </div>
                <span className={`trainer-group-status-badge trainer-group-status-${project.status}`}>
                  {project.status}
                </span>
              </div>

              {/* Project Title and Description */}
              <div className="trainer-group-project-content">
                <h3 className="trainer-group-project-title">{project.name}</h3>
                <p className="trainer-group-project-description">{project.description}</p>
              </div>

              {/* Project Details */}
              <div className="trainer-group-project-details">
                <div className="trainer-group-detail-item">
                  <Calendar size={16} className="trainer-group-detail-icon" />
                  <span>Due {project.deadline ? new Date(project.deadline).toLocaleDateString() : 'No deadline'}</span>
                </div>
                <div className="trainer-group-detail-item">
                  <Users size={16} className="trainer-group-detail-icon" />
                  <span>{project.teams || project.maxTeams} teams • {project.membersPerTeam} members each</span>
                </div>
                <div className="trainer-group-detail-item">
                  <FileText size={16} className="trainer-group-detail-icon" />
                  <span>{project.deliverables} deliverables</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="trainer-group-project-actions">
                <button
                  className="trainer-group-btn trainer-group-btn-view"
                  onClick={() => onViewProject?.(project)}
                >
                  <Eye size={16} />
                  View
                </button>
                <button
                  className="trainer-group-btn trainer-group-btn-edit"
                  onClick={() => onEditProject?.(project)}
                >
                  <Edit size={16} />
                  Edit
                </button>
              </div>

              {/* Footer */}
              <div className="trainer-group-project-footer">
                Created {project.createdDate ? new Date(project.createdDate).toLocaleDateString() : 'N/A'}
              </div>
            </div>
          ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="trainer-group-pagination">
                <button
                  className="trainer-group-pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={20} />
                  Previous
                </button>

                <div className="trainer-group-pagination-pages">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      className={`trainer-group-pagination-page ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="trainer-group-pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight size={20} />
                </button>
              </div>
            )}

            {/* Results Info */}
            <div className="trainer-group-results-info">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredProjects.length)} of {filteredProjects.length} projects
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectList;